'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { fetchCountries, uploadFile } from '@eliadmin/core/src/api/general/general-api';
import { Button } from '@eliadmin/core/src/components/ui/button';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { initCustomerStore, useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useMemo, useRef, useState } from 'react';
import type { Control, FieldPath } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, number as __eliadmin_zod_2, boolean as __eliadmin_zod_3 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';
import { LiaEnvelope } from 'react-icons/lia';
import { MdOutlineLocalPhone } from 'react-icons/md';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
type ProfileEditFormValues = z.infer<ReturnType<typeof profileEditSchema>>;
export function ProfileEditForm() {
  const customer = useCustomerStore(s => s.me!.customer);
  const authToken = useCustomerStore(s => s.me!.authToken);

  const ref = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | undefined>(undefined);

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ProfileEditFormValues>({
    resolver: zodResolver(profileEditSchema(formValidationMessages)),
    defaultValues: {
      name: customer?.name ?? '',
      surname: customer?.surname ?? '',
      email: customer?.email ?? '',
      phone: customer?.phone ?? '',
      gender: customer?.gender ?? undefined,
      address: customer?.address ?? '',
      street_number: customer?.street_number ?? '',
      city: customer?.city ?? '',
      postcode: customer?.postcode ?? '',
      country: customer?.country ?? undefined,
      iban: customer?.iban ?? '',
      swift: customer?.swift ?? '',
      address_delivery: customer?.address_delivery ?? '',
      street_number_delivery: customer?.street_number_delivery ?? '',
      postcode_delivery: customer?.postcode_delivery ?? '',
      city_delivery: customer?.city_delivery ?? '',
      country_delivery: customer?.country_delivery ?? undefined,
      company: customer?.company ?? '',
      ico: customer?.ico ?? '',
      dic: customer?.dic ?? '',
      ic_dph: customer?.ic_dph ?? '',
      company_buy: customer?.company_buy ?? false,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: updateCustomer,
  });

  const handleSubmit = form.handleSubmit(async data => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await uploadFile({
          authToken,
          formData,
          locale,
        });
        data.image = res.data;
      } catch (e) {
        //TODO: handle error / create custom field for this error
        return;
      }
    }

    try {
      await mutateAsync({
        locale,
        data: data as any,
        authToken,
      }).then(res => {
        const typedRes = res as { data: Customer };
        typedRes &&
          initCustomerStore({
            authToken,
            customer: { ...typedRes?.data },
          });
        return typedRes;
      });

      router.refresh();
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
    }
  });

  return (
    <Form {...form}>
      <form
        className={cn(
          'grid grid-cols-12 gap-6 sm:gap-8 md:gap-10 lg:gap-12',
          'ea-profile-edit-form',
        )}
        onSubmit={handleSubmit}
      >
        <div className="col-span-12 flex w-full flex-col items-start justify-start gap-2">
          <strong className="text-2xl font-medium tracking-tight sm:text-3xl">
            {customer.name} {customer.surname}
          </strong>
          <p className="text-sm sm:text-[#636363]">{customer.email}</p>
        </div>
        <FormSection>
          <Field
            required
            control={form.control}
            name="name"
            placeholder={t('text33')}
            className="col-span-12 w-full sm:col-span-6"
          />
          <Field
            required
            control={form.control}
            name="surname"
            placeholder={t('text34')}
            className="col-span-12 w-full sm:col-span-6"
          />
        </FormSection>
        <FormSection title={t('text360')}>
          <Field
            required
            control={form.control}
            name="email"
            type="email"
            placeholder={t('text35')}
            before={<LiaEnvelope size={24} className="text-[#636363]" />}
            className="col-span-12 w-full sm:col-span-6"
          />
          <Field
            control={form.control}
            name="phone"
            placeholder={t('text36')}
            before={<MdOutlineLocalPhone size={24} className="text-[#636363]" />}
            className="col-span-12 w-full sm:col-span-6"
          />
        </FormSection>
        <FormSection title={t('text157')}>
          <Field
            control={form.control}
            name="address"
            placeholder={t('text38')}
            className="col-span-12 w-full sm:col-span-8"
          />
          <Field
            control={form.control}
            name="street_number"
            placeholder={t('text438')}
            className="col-span-12 w-full sm:col-span-4"
          />
          <Field
            control={form.control}
            name="postcode"
            placeholder={t('text40')}
            className="col-span-12 w-full sm:col-span-4"
          />
          <Field
            control={form.control}
            name="city"
            placeholder={t('text39')}
            className="col-span-12 w-full sm:col-span-8"
          />
          <Field
            control={form.control}
            type="select"
            name="country.id"
            placeholder={t('text519')}
            isDisabled={isCountriesLoading}
            className="col-span-12 w-full"
            options={countries.data.map(c => ({
              value: c.id,
              label: c.name,
            }))}
          />
        </FormSection>
        <div className="col-span-12 flex w-full items-center justify-start gap-4">
          <FormField
            control={form.control}
            name="company_buy"
            render={({ field }) => (
              <FormItem className="flex w-full items-center gap-2">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    id="company_buy"
                    name="company_buy"
                  />
                </FormControl>
                <FormLabel htmlFor="company_buy" className=" whitespace-nowrap">
                  {t('text2362')}
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* TODO: countries */}

        <FormSection title={t('text679')}>
          <Field
            control={form.control}
            name="ico"
            placeholder={t('text42')}
            className="col-span-12 w-full sm:col-span-6"
          />
          <Field
            control={form.control}
            name="dic"
            placeholder={t('text43')}
            className="col-span-12 w-full sm:col-span-6"
          />
          <Field
            control={form.control}
            name="ic_dph"
            placeholder={t('text44')}
            className="col-span-12 w-full"
          />
          <Field
            control={form.control}
            name="company"
            placeholder={t('text37')}
            className="col-span-12 w-full"
          />
          <Field
            control={form.control}
            name="address_delivery"
            placeholder={t('text38')}
            className="col-span-12 w-full sm:col-span-8"
          />
          <Field
            control={form.control}
            name="street_number_delivery"
            placeholder={t('text438')}
            className="col-span-12 w-full sm:col-span-4"
          />
          <Field
            control={form.control}
            name="postcode_delivery"
            placeholder={t('text40')}
            className="col-span-12 w-full sm:col-span-4"
          />
          <Field
            control={form.control}
            name="city_delivery"
            placeholder={t('text39')}
            className="col-span-12 w-full sm:col-span-8"
          />
          <Field
            control={form.control}
            type="select"
            name="country_delivery.id"
            placeholder={t('text519')}
            isDisabled={isCountriesLoading}
            className="col-span-12 w-full"
            options={countries.data.map(c => ({
              value: c.id,
              label: c.name,
            }))}
          />
        </FormSection>

        <div className="col-span-12 flex flex-row justify-start">
          <Button
            type="submit"
            variant={'success'}
            className="w-full md:w-auto"
            isLoading={form.formState.isSubmitting}
          >
            {t('text201')}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function FormSection({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <div className="col-span-12 grid w-full grid-cols-12 gap-6">
      {title && (
        <h4 className="col-span-12 w-full text-xl font-medium tracking-tight sm:text-2xl">
          {title}
        </h4>
      )}
      {children}
    </div>
  );
}

type FieldProps = {
  control: Control<ProfileEditFormValues>;
  name: FieldPath<ProfileEditFormValues>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  type?: InputProps['type'] | 'tel' | 'radio';
  options?: { value: string | number; label: string }[];
  isFullWidth?: boolean;
  isDisabled?: boolean;
  before?: React.ReactNode;
  after?: React.ReactNode;
  className?: string;
};

function Field(props: FieldProps) {
  const content = useMemo(() => {
    if (props.type === 'select') {
      if (!props.options) {
        throw new Error('Select type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="p-[1px]">
                <FormLabel>{props.label}</FormLabel>
                <FormControl>
                  <Select
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                  >
                    <SelectTrigger
                      className="h-[50px] w-full overflow-hidden rounded-full bg-black/[0.03] px-6"
                      innerTriggerClassName="p-0"
                    >
                      <SelectValue placeholder={props.placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {props.options?.map(o => (
                        <SelectItem key={o.value} value={String(o.value)}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'radio') {
      if (!props.options) {
        throw new Error('Radio type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="flex flex-col gap-2 p-[1px]">
                <FormLabel>{props.label}</FormLabel>
                <FormControl>
                  <RadioGroup
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                    className="flex flex-row gap-4 pl-2"
                  >
                    {props.options?.map(o => (
                      <div key={o.value} className="flex gap-2">
                        <RadioGroupItem id={`radiogroupitem-${o.value}`} value={String(o.value)} />
                        <Label className="flex items-center" htmlFor={`radiogroupitem-${o.value}`}>
                          <p className="text-sm font-semibold text-foreground">{o.label}</p>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'tel') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              <FormLabel>{props.label}</FormLabel>
              <FormControl>
                <PhoneInputField
                  placeholder={props.placeholder}
                  type={props.type}
                  {...(field as any)}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem required={props.required} className={cn('p-[1px]', props.className)}>
            <FormLabel>{props.label}</FormLabel>
            <FormControl>
              <Input
                placeholder={props.placeholder}
                type={props.type}
                {...(field as any)}
                disabled={props.isDisabled}
                aria-disabled={props.isDisabled}
                before={props.before}
                after={props.after}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }, [
    props.control,
    props.isDisabled,
    props.required,
    props.label,
    props.name,
    props.options,
    props.placeholder,
    props.type,
  ]);

  return (
    <div
      className={cn('w-full', props.className, {
        ['md:col-span-12']: props.isFullWidth,
      })}
    >
      {content}
    </div>
  );
}

export const profileEditSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    name: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    company: __eliadmin_zod_1().optional(),
    phone: __eliadmin_zod_1().optional(),
    email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    username: __eliadmin_zod_1().optional(),
    address: __eliadmin_zod_1().optional(),
    street_number: __eliadmin_zod_1().optional(),
    postcode: __eliadmin_zod_1().optional(),
    city: __eliadmin_zod_1().optional(),
    country: __eliadmin_zod_0({
        id: __eliadmin_zod_1().or(__eliadmin_zod_2()).optional(),
      })
      .optional(),
    company_buy: __eliadmin_zod_3().optional(),
    ico: __eliadmin_zod_1().optional(),
    dic: __eliadmin_zod_1().optional(),
    ic_dph: __eliadmin_zod_1().optional(),
    gender: __eliadmin_zod_0({
        id: __eliadmin_zod_1().or(__eliadmin_zod_2()).optional(),
      })
      .optional(),
    image: __eliadmin_zod_0({
        id: __eliadmin_zod_2(),
      })
      .optional(),
    address_temporary: __eliadmin_zod_1().optional(),
    street_number_temporary: __eliadmin_zod_1().optional(),
    postcode_temporary: __eliadmin_zod_1().optional(),
    city_temporary: __eliadmin_zod_1().optional(),
    country_temporary: __eliadmin_zod_0({
        id: __eliadmin_zod_1(),
      })
      .optional(),
    email_company: __eliadmin_zod_1().email().optional(),
    address_company: __eliadmin_zod_1().optional(),
    street_number_company: __eliadmin_zod_1().optional(),
    postcode_company: __eliadmin_zod_1().optional(),
    city_company: __eliadmin_zod_1().optional(),
    country_company: __eliadmin_zod_0({
        id: __eliadmin_zod_1(),
      })
      .optional(),
    address_delivery: __eliadmin_zod_1().optional(),
    street_number_delivery: __eliadmin_zod_1().optional(),
    postcode_delivery: __eliadmin_zod_1().optional(),
    city_delivery: __eliadmin_zod_1().optional(),
    country_delivery: __eliadmin_zod_0({
        id: __eliadmin_zod_1(),
      })
      .optional(),
    name_delivery: __eliadmin_zod_1().optional(),
    surname_delivery: __eliadmin_zod_1().optional(),
    company_delivery: __eliadmin_zod_1().optional(),
    iban: __eliadmin_zod_1().optional(),
    swift: __eliadmin_zod_1().optional(),
    instagram: __eliadmin_zod_1().optional(),
    facebook: __eliadmin_zod_1().optional(),
    national_id_number: __eliadmin_zod_1().optional(),
  });
